<template>
  <div id="WebSocket" v-if="devStatus" class="api-root">
    <!-- Floating API button -->
    <button
      class="api-fab"
      @click="showConsole = !showConsole"
      :aria-expanded="showConsole.toString()"
      aria-controls="api-console"
    >
      API
    </button>

    <!-- Slide/fade console overlay (renders only when open) -->
    <transition name="ws-slide-fade" mode="out-in">
      <aside
        v-if="showConsole"
        id="api-console"
        class="api-console"
        role="dialog"
        aria-label="API Console"
      >
        <header class="api-header">
          <h1>API</h1>
          <button
            class="close-btn"
            @click="showConsole = false"
            aria-label="Close API console"
          >
            ✕
          </button>
        </header>

        <label for="command2">Message</label>
        <input type="text" id="command2" v-model="userData.command" />

        <label for="datasend">Data</label>
        <textarea id="datasend" rows="4" v-model="userData.data"></textarea>

        <button class="send-btn" @click="do_command()">Send Command</button>

        <label for="datarecv">Received</label>
        <textarea id="datarecv" v-model="dataRecv"></textarea>

        <details v-if="jwt" class="jwt-wrap">
          <summary>JWT Token</summary>
          <p id="jwt_token">{{ jwt }}</p>
        </details>
      </aside>
    </transition>
  </div>
</template>

<script>
  import store from '@/store/index';
  import awsInfo from '@/utils/websocket';

  export default {
    name: 'WebSocket',
    props: {
      token: {
        type: Object,
      },
    },
    data() {
      return {
        ws: awsInfo.aws,
        userData: { command: 'GetDeviceList', data: '{}' },
        dataRecv: '',
        showConsole: false,
      };
    },
    filters: {
      pretty(value) {
        return JSON.stringify(JSON.parse(value), null, 2);
      },
    },
    methods: {
      receive_cb(o) {
        this.$emit('receivedMessageEvent', o);
        this.dataRecv = `${this.dataRecv}\nRECV: ${JSON.stringify(o)}\n`;
      },
      sent_cb(o) {
        this.dataRecv = `${this.dataRecv}\nSEND: ${JSON.stringify(o)}\n`;
      },
      do_command() {
        awsInfo.aws.send_command(this.userData.command, this.userData.data);
      },
      jwt_cb() {
        return new Promise((resolve) => resolve(store.getters.getToken));
      },
      token_update_cb(newToken) {
        console.log('Updating Token');
        store.commit('setToken', newToken);
      },
    },
    computed: {
      devStatus() {
        return process.env.NODE_ENV === 'development';
      },
      jwt() {
        return store.getters.getToken;
      },
    },
    created() {
      console.log('WEBSOCKET: Created...');
      awsInfo.connect(
        this.receive_cb,
        this.sent_cb,
        this.jwt_cb,
        this.token_update_cb
      );
    },
    mounted() {
      console.log('WEBSOCKET: Mounted...');
    },
    beforeUnmount() {
      console.log('WEBSOCKET: Destroy...');
      awsInfo.disconnect();
    },
  };
</script>

<style scoped>
  * {
    --border: 2px solid rgba(0, 0, 0, 0.15);
  }

  /* Floating button bottom-right */
  .api-fab {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 9999;
    cursor: pointer;
    border: var(--border);
    border-radius: 999px;
    background: var(#fff);
    padding: 0.6rem 0.9rem;
  }

  /* Console overlay (fixed so it doesn't affect page layout) */
  .api-console {
    display: grid;
    position: fixed;
    right: 1rem;
    bottom: 4rem; /* sits above the button */
    gap: 0.6rem;
    z-index: 9998;
    backdrop-filter: blur(3px);
    box-sizing: border-box;
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: rgba(255, 255, 255, 0.9);
    padding: 1rem;
    width: min(1200px, 90vw);
    max-height: min(80vh, 720px);
    overflow: auto; /* scrollbars appear only inside when open */
    overscroll-behavior: contain;
    color: black;
  }

  h1 {
    font-size: 2rem;
  }

  .api-console,
  .api-console * {
    scrollbar-color: var(--bg) rgba(255, 255, 255, 0.9);
  }

  .api-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  .close-btn {
    cursor: pointer;
    border: var(--border);
    background: transparent;
    font-size: 1.1rem;
    line-height: 1;
  }

  /* Inputs fill panel width without causing horizontal overflow */
  .api-console input[type='text'],
  .api-console textarea {
    box-sizing: border-box;
    border: var(--border);
    border-radius: var(--border-radius);
    background: transparent;
    padding: 0.5rem 0.6rem;
    width: 100%;
    max-width: 100%;
    font-size: 0.95rem;
    font-family: inherit;
  }

  /* Cap the big log textarea to fit the panel nicely */
  #datarecv {
    height: 40vh; /* ignores rows to avoid pushing page */
    resize: vertical;
  }

  .send-btn {
    align-self: start;
    cursor: pointer;
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--btn-bg, #f6f6f6);
    padding: 0.5rem 0.8rem;
  }

  .jwt-wrap {
    margin-top: 0.4rem;
  }

  #jwt_token {
    border-radius: var(--border-radius);
    background: #f7f7f7;
    padding: 0.4rem 0.6rem;
    word-break: break-all;
  }

  /* Subtle slide/fade for the overlay */
  .ws-slide-fade-enter-active,
  .ws-slide-fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .ws-slide-fade-enter-from,
  .ws-slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }

  details summary {
    cursor: pointer;
    user-select: none;
  }
</style>
